import React, { useEffect, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { AlertTriangle, ArrowRight } from 'lucide-react';
import api from '../../lib/api';
import { computeChainLeak } from '../../lib/chainLeak';

// Stage-specific modelling destinations. Behavior has a dedicated
// modeller; every other stage starts from the Solution Finder.
const STAGE_MODEL_ROUTE = { behavior: '/roe-modeller' };

/**
 * CurrentOpportunityBanner — continuity from Mission Control Home
 * into Impact Modelling. Surfaces the live Active Value Constraint
 * (steepest QoQ chain decline) as a modelling prompt, with the same
 * maturity-attenuated "Expected Value Recovery" estimate the Home
 * card shows. Self-hides when no chain stage declined this quarter.
 */
export default function CurrentOpportunityBanner() {
  const navigate = useNavigate();
  const [deltas, setDeltas] = useState(null);
  const [maturityFin, setMaturityFin] = useState(null);

  useEffect(() => {
    let cancelled = false;
    api.get('/health/quarter-deltas')
      .then((r) => { if (!cancelled) setDeltas(r.data); })
      .catch(() => {});
    api.get('/maturity-assessment/financial')
      .then((r) => { if (!cancelled) setMaturityFin(r.data); })
      .catch(() => {});
    return () => { cancelled = true; };
  }, []);

  const leak = computeChainLeak(deltas?.metrics);
  if (!leak) return null;

  const leakPct = Math.abs(Math.round(leak.deltaPct));
  const realizedPct = maturityFin?.has_assessment
    ? (maturityFin?.investment_efficiency?.current_pct
        ?? (maturityFin?.maturity_factor?.current != null ? maturityFin.maturity_factor.current * 100 : null))
    : null;
  const attenuated = typeof realizedPct === 'number' && realizedPct > 0;
  const recoveryPct = attenuated ? Math.max(1, Math.round(leakPct * (realizedPct / 100))) : leakPct;
  const modelRoute = STAGE_MODEL_ROUTE[leak.key] || '/solution-finder';
  // Carry the constraint context into the modelling destination so it
  // can pre-fill targets and frame the session (ConstraintContextBanner).
  const contextParams = new URLSearchParams({
    constraint_stage: leak.key,
    constraint_label: leak.stage.label,
    leak: String(leakPct),
    recovery: String(recoveryPct),
    attenuated: attenuated ? '1' : '0',
  });

  return (
    <section
      className="rounded-2xl border border-orange-200 bg-gradient-to-r from-amber-50 via-orange-50/70 to-amber-50 p-5 shadow-sm"
      data-testid="current-opportunity-banner"
    >
      <div className="flex items-center gap-4 flex-wrap lg:flex-nowrap">
        <span className="flex h-10 w-10 flex-shrink-0 items-center justify-center rounded-full bg-orange-100 ring-1 ring-orange-200">
          <AlertTriangle className="h-5 w-5 text-orange-600" />
        </span>
        <div className="flex-1 min-w-[240px]">
          <p className="text-[11px] font-bold uppercase tracking-[0.16em] text-orange-700">Current Highest Opportunity</p>
          <p className="text-base font-bold text-slate-900 leading-snug mt-0.5" data-testid="opportunity-headline">
            {leak.stage.label} is currently reducing value transfer by {leakPct}%.
          </p>
          <p className="text-sm text-slate-600 mt-0.5">Model potential interventions to estimate recovery.</p>
        </div>
        <div className="flex items-center gap-x-8 gap-y-3 flex-wrap">
          <div data-testid="opportunity-recovery">
            <p className="text-[10px] font-semibold uppercase tracking-wider text-slate-500">Expected Value Recovery</p>
            <p
              className="text-xl font-extrabold text-emerald-600 mt-0.5 tabular-nums"
              title={attenuated
                ? `The ${leakPct}% leak attenuated by ~${Math.round(realizedPct)}% maturity realization — the defensible estimate, not the theoretical maximum.`
                : 'Theoretical maximum — complete a Maturity Assessment to attenuate this into a defensible estimate.'}
            >
              {attenuated ? `+${recoveryPct}%` : `up to +${recoveryPct}%`}
            </p>
          </div>
          <button
            type="button"
            onClick={() => navigate(`${modelRoute}?${contextParams.toString()}`)}
            className="inline-flex items-center gap-1.5 rounded-lg border border-orange-300 bg-white px-3.5 py-2 text-sm font-bold text-orange-700 hover:bg-orange-50 transition-colors"
            data-testid="opportunity-model-btn"
          >
            Model {leak.stage.label} Improvement <ArrowRight className="h-3.5 w-3.5" />
          </button>
        </div>
      </div>
    </section>
  );
}
